import { useCallback, useState } from 'react';
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { Button } from '@client/components/ui/button';
import { carePhaseDefList } from '@shared';

export const YEAR_PRESETS = [5, 10, 15, 20] as const;
export const CURRENT_YEAR = new Date().getFullYear();

export function isPresetYear(value: number | null | undefined): boolean {
  return value != null && YEAR_PRESETS.some(y => CURRENT_YEAR + y === value);
}

function CustomYearInput({
  value,
  onChange,
  disabled,
  placeholder = 'e.g. 2035',
  className = 'flex-1',
}: {
  value: number | null | undefined;
  onChange: (year: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const [text, setText] = useState(value != null ? String(value) : '');

  return (
    <Input
      type="number"
      placeholder={placeholder}
      value={text}
      onChange={e => {
        const newText = e.target.value.slice(0, 4);
        setText(newText);
        if (newText !== '') {
          onChange(Number(newText));
        }
      }}
      disabled={disabled}
      className={className}
    />
  );
}

export function YearPresetSelect({
  label,
  defaultLabel,
  value,
  onChange,
  disabled,
}: {
  label: string;
  defaultLabel: string;
  value: number | null | undefined;
  onChange: (year: number | null) => void;
  disabled?: boolean;
}) {
  const [isCustom, setIsCustom] = useState(() => value != null && !isPresetYear(value));

  const selectValue = value == null ? 'default' : isCustom ? 'custom' : String(value);

  const handleSelectChange = useCallback(
    (val: string) => {
      if (val === 'default') {
        setIsCustom(false);
        onChange(null);
      } else if (val === 'custom') {
        setIsCustom(true);
      } else {
        setIsCustom(false);
        onChange(Number(val));
      }
    },
    [onChange],
  );

  if (isCustom) {
    return (
      <div className="space-y-1.5">
        <Label className="inline-block text-[10px] font-semibold uppercase">{label}</Label>
        <div className="flex gap-2">
          <CustomYearInput
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setIsCustom(false);
              onChange(null);
            }}
            className="shrink-0"
          >
            Reset
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label className="inline-block text-[10px] font-semibold uppercase">{label}</Label>
      <Select
        value={selectValue}
        onValueChange={handleSelectChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={defaultLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="default">{defaultLabel}</SelectItem>
          {YEAR_PRESETS.map(years => (
            <SelectItem
              key={years}
              value={String(CURRENT_YEAR + years)}
            >
              In {years} years ({CURRENT_YEAR + years})
            </SelectItem>
          ))}
          <SelectItem value="custom">Custom year</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

export interface CarePhaseYears {
  earlyCare: number | null;
  moderateCare: number | null;
  fullCare: number | null;
}

export function CarePhaseYearSelect({
  label,
  defaultLabel,
  value,
  onChange,
  disabled,
  carePhaseYears,
  ltcEndYear,
}: {
  label: string;
  defaultLabel: string;
  value: number | null | undefined;
  onChange: (year: number | null) => void;
  disabled?: boolean;
  carePhaseYears: CarePhaseYears;
  ltcEndYear?: number | null;
}) {
  const presetYears = carePhaseDefList
    .map(def => ({
      key: def.key,
      label: def.label,
      year: carePhaseYears[def.key as keyof CarePhaseYears],
    }))
    .filter(p => p.year != null)
    .map(p => ({ ...p, year: Math.round(p.year!) }));

  const hasPresets = presetYears.length > 0;

  const isPreset =
    value != null &&
    (presetYears.some(p => p.year === value) || (ltcEndYear != null && value === ltcEndYear));
  const [isCustom, setIsCustom] = useState(() => value != null && !isPreset);

  const selectValue = value == null ? 'default' : isCustom ? 'custom' : String(value);

  const handleSelectChange = useCallback(
    (val: string) => {
      if (val === 'default') {
        setIsCustom(false);
        onChange(null);
      } else if (val === 'custom') {
        setIsCustom(true);
      } else {
        setIsCustom(false);
        onChange(Number(val));
      }
    },
    [onChange],
  );

  if (!hasPresets) {
    return (
      <div className="space-y-1.5">
        <Label className="inline-block text-[10px] font-semibold uppercase">{label}</Label>
        {value != null ? (
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder="e.g. 2035"
              value={value ?? ''}
              onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
              onInput={e => {
                const input = e.target as HTMLInputElement;
                if (input.value.length > 4) input.value = input.value.slice(0, 4);
              }}
              disabled={disabled}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => onChange(null)}
              className="shrink-0"
            >
              Reset
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder={defaultLabel}
              value=""
              onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
              onInput={e => {
                const input = e.target as HTMLInputElement;
                if (input.value.length > 4) input.value = input.value.slice(0, 4);
              }}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    );
  }

  if (isCustom) {
    return (
      <div className="space-y-1.5">
        <Label className="inline-block text-[10px] font-semibold uppercase">{label}</Label>
        <div className="flex gap-2">
          <CustomYearInput
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setIsCustom(false);
              onChange(null);
            }}
            className="shrink-0"
          >
            Reset
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label className="inline-block text-[10px] font-semibold uppercase">{label}</Label>
      <Select
        value={selectValue}
        onValueChange={handleSelectChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={defaultLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="default">{defaultLabel}</SelectItem>
          {presetYears.map(p => (
            <SelectItem
              key={p.key}
              value={String(p.year)}
            >
              Start of {p.label} ({p.year})
            </SelectItem>
          ))}
          {ltcEndYear != null && (
            <SelectItem value={String(ltcEndYear)}>At LTC end ({ltcEndYear})</SelectItem>
          )}
          <SelectItem value="custom">Custom year</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
